import { formatNumber, type MrpOverstockRow } from "@/lib/mrp";

function formatCurrency(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatUnitPrice(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

export function MrpOverstockValueHoverHintBody({ row }: { row: MrpOverstockRow }): JSX.Element {
  if (row.unit_price == null) {
    return <span className="text-slate-600">No unit price available for this SKU.</span>;
  }

  const sourceCurrency = row.unit_price_currency?.trim().toUpperCase() ?? "USD";
  const sourceAmount = row.unit_price_source_amount;
  const convertedFromCny =
    sourceCurrency === "CNY" &&
    sourceAmount != null &&
    Number.isFinite(sourceAmount) &&
    Math.abs(sourceAmount - row.unit_price) > 0.001;

  return (
    <div className="space-y-2 text-xs text-slate-800">
      <p className="font-medium text-slate-900">Supply position value (USD)</p>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th className="pb-1 pr-3 font-medium"> </th>
            <th className="pb-1 pr-3 text-right font-medium">Qty</th>
            <th className="pb-1 text-right font-medium">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="py-0.5 pr-3">On hand</td>
            <td className="py-0.5 pr-3 text-right tabular-nums">{formatNumber(row.on_hand)}</td>
            <td className="py-0.5 text-right tabular-nums">{formatCurrency(row.on_hand_value)}</td>
          </tr>
          <tr>
            <td className="py-0.5 pr-3">On order</td>
            <td className="py-0.5 pr-3 text-right tabular-nums">{formatNumber(row.on_order)}</td>
            <td className="py-0.5 text-right tabular-nums">{formatCurrency(row.on_order_value)}</td>
          </tr>
          <tr className="border-t border-slate-200 font-medium">
            <td className="pt-1 pr-3">Total</td>
            <td className="pt-1 pr-3 text-right tabular-nums">{formatNumber(row.supply_position)}</td>
            <td className="pt-1 text-right tabular-nums">{formatCurrency(row.overstock_value)}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-slate-600">
        Unit price {formatUnitPrice(row.unit_price)}
        {convertedFromCny ? (
          <>
            {" "}
            (from {sourceAmount!.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{" "}
            {sourceCurrency} on latest purchase order)
          </>
        ) : null}
      </p>
    </div>
  );
}
